import $i18n from 'panda-i18n';
import classNames from 'classnames';
import React, { forwardRef, Fragment, useCallback, useImperativeHandle, useState, } from 'react';
import { CnButton } from '@/components/cn-button';
import { CnIcon } from '@/components/cn-icon';
import { CnSafeArea } from '@/components/cn-safe-area';
import { CnDrawer } from '@/components/cn-drawer';
import { BottomButton } from '../bottom-button';
import isString from 'lodash/isString';
import { stopPropagation } from '@/utils/func';
import { getValidChildren } from '@/utils/children';
const SelectDrawer = (props, ref) => {
    const { prefix = 'cn-ui-m-', align: alignProp, children, drawerTitle = '', iconType: iconTypeProp, content = '', placeholder, type = 'normal', buttonPosition = 'top', className, drawerClassName, formLabel, insideForm, insideFilter, disabled = false, readOnly = false, showIcon = true, hasClear = false, hideButton, showToolbar: showToolbarProp = true, notFoundContent, onOk, onCancel, onClear, onVisibleChange, okText, cancelText, zIndex, size: sizeProp, disableScroll, ...others } = props;
    const [visible, setVisible] = useState(false);
    const _children = getValidChildren(children);
    const align = alignProp || 'left';
    const size = sizeProp || 'medium';
    const shouldShowClear = !disabled && hasClear && content;
    const shouldShowIcon = showIcon && !shouldShowClear;
    const isInverse = type === 'inverse';
    const iconType = iconTypeProp || (insideFilter ? 'triangle-down-fill' : 'arrow-right');
    // notice: showToolbarProp 为 false 是，直接隐藏 title 和所有按钮
    const showToolbar = showToolbarProp && buttonPosition === 'top' && !hideButton;
    const showBottomBtn = showToolbarProp && buttonPosition === 'bottom' && !hideButton;
    const clsPrefix = `${prefix}selectdrawer`;
    const doHide = useCallback((reason) => {
        if (visible) {
            setVisible(false);
        }
        if (onVisibleChange) {
            onVisibleChange(false, reason);
        }
    }, [visible, setVisible, onVisibleChange]);
    const handleOk = useCallback(() => {
        let shouldHide = true;
        if (onOk) {
            shouldHide = onOk();
        }
        if (shouldHide !== false) {
            doHide();
        }
    }, [onOk, doHide]);
    const handleCancel = useCallback((reason) => {
        let shouldHide = true;
        if (onCancel) {
            shouldHide = onCancel(reason);
        }
        if (shouldHide !== false) {
            doHide(reason);
        }
    }, [onCancel, doHide]);
    const handleClear = useCallback((e) => {
        e.preventDefault();
        stopPropagation(e);
        if (onClear) {
            onClear();
        }
    }, [onClear]);
    const handleOpen = () => {
        if (disabled || readOnly || visible) {
            return;
        }
        if (_children || notFoundContent) {
            setVisible(true);
            if (onVisibleChange) {
                onVisibleChange(true, undefined);
            }
        }
    };
    useImperativeHandle(ref, () => {
        return Object.assign(ref.current || {}, {
            hide: doHide,
            show: handleOpen,
            ok: handleOk,
            cancel: handleCancel,
        });
    });
    const getContent = () => {
        if (!content) {
            return (React.createElement("div", { className: classNames({
                    [`${clsPrefix}-placeholder`]: true,
                    [`${clsPrefix}-placeholder--${align}`]: true,
                    [`${clsPrefix}-placeholder--disabled`]: disabled,
                    [`${clsPrefix}-placeholder--inverse`]: isInverse,
                    [`${clsPrefix}-placeholder--inverse-disabled`]: disabled && isInverse,
                }) }, placeholder ||
                $i18n.get({ id: 'PleaseSelect', dm: '请选择', ns: 'CnSelect' })));
        }
        else if (isString(content)) {
            return (React.createElement("span", { className: classNames({
                    [`${clsPrefix}-content-text`]: true,
                    [`${clsPrefix}-content-text--${align}`]: true,
                    [`${clsPrefix}-content-text--${size}`]: size,
                    [`${clsPrefix}-content-text--disabled`]: disabled,
                    [`${clsPrefix}-content-text--inverse`]: isInverse,
                    [`${clsPrefix}-content-text--inverse-disabled`]: isInverse && disabled,
                }), style: {
                    textAlign: align,
                } }, content));
        }
        else {
            return content;
        }
    };
    const renderChildren = () => {
        let ret = null;
        if (_children) {
            ret = React.createElement("div", { className: `${clsPrefix}-drawer-content` }, _children);
        }
        else if (isString(notFoundContent)) {
            ret = (React.createElement("div", { className: `${clsPrefix}-drawer-content ${clsPrefix}-drawer--empty` },
                React.createElement("span", { className: `${clsPrefix}-drawer-empty-text` }, notFoundContent)));
        }
        else if (notFoundContent) {
            ret = (React.createElement("div", { className: `${clsPrefix}-drawer-content ${clsPrefix}-drawer--empty` },
                React.createElement("div", { className: `${clsPrefix}-drawer-empty-text` }, notFoundContent)));
        }
        return ret;
    };
    return (React.createElement(Fragment, null,
        React.createElement("div", { ref: ref, ...others, className: classNames(clsPrefix, `${clsPrefix}--${size}`, className, {
                [`${clsPrefix}-form`]: insideForm,
                [`${clsPrefix}-filter`]: insideFilter,
            }), onClick: handleOpen },
            React.createElement("div", { className: classNames(`${clsPrefix}-content`, `${clsPrefix}-content--${size}`, `${clsPrefix}-content--${align}`) }, getContent()),
            shouldShowIcon ? (React.createElement("div", { className: classNames(`${clsPrefix}-icon`, `${clsPrefix}-icon--${size}`) },
                React.createElement(CnIcon, { className: classNames(`${clsPrefix}-icon-text`, `${clsPrefix}-icon-text--${size}`, {
                        [`${clsPrefix}-icon-text--inverse`]: isInverse,
                        [`${clsPrefix}-icon-text--disabled`]: disabled,
                        [`${clsPrefix}-icon-text--inverse-disabled`]: isInverse && disabled,
                    }), type: iconType }))) : null,
            shouldShowClear ? (React.createElement("div", { className: classNames(`${clsPrefix}-icon`, `${clsPrefix}-icon--${size}`), onClick: handleClear },
                React.createElement(CnIcon, { className: classNames(`${clsPrefix}-icon-text--${size}`, {
                        [`${clsPrefix}-icon-text--inverse`]: isInverse,
                        [`${clsPrefix}-icon-text--disabled`]: disabled,
                        [`${clsPrefix}-icon-text--inverse-disabled`]: isInverse && disabled,
                    }), type: "error-r-fill" }))) : null),
        React.createElement(CnDrawer, { visible: visible, title: drawerTitle || formLabel, placement: "bottom", closeMode: drawerTitle || formLabel ? ['mask', 'close'] : ['mask'], onClose: handleCancel, zIndex: zIndex, className: classNames(`${clsPrefix}-drawer`, drawerClassName), containerClassName: `${clsPrefix}-container`, noCard: true },
            showToolbar ? (React.createElement("div", { className: classNames(`${clsPrefix}-toolbar`, {
                    [`${clsPrefix}-toolbar--no-border`]: buttonPosition === 'bottom',
                }) },
                !hideButton && buttonPosition !== 'bottom' ? (React.createElement(CnButton, { className: `${clsPrefix}-toolbar-btn-cancel`, text: true, onClick: () => handleCancel('cancel-button') }, cancelText ||
                    $i18n.get({ id: 'Cancel', dm: '取消', ns: 'CnSelect' }))) : null,
                React.createElement("div", { className: `${clsPrefix}-toolbar-title` }, drawerTitle),
                !hideButton && buttonPosition !== 'bottom' ? (React.createElement(CnButton, { className: `${clsPrefix}-toolbar-btn-ok`, text: true, type: "primary", onClick: handleOk }, okText ||
                    $i18n.get({ id: 'Confirm', dm: '确认', ns: 'CnSelect' }))) : null)) : null,
            renderChildren(),
            showBottomBtn ? (React.createElement(BottomButton, { okText: okText || $i18n.get({ id: 'Confirm', dm: '确认', ns: 'CnSelect' }), cancelText: cancelText ||
                    $i18n.get({ id: 'Cancel', dm: '取消', ns: 'CnSelect' }), onOk: handleOk, onCancel: () => handleCancel('cancel-button'), enableSafeArea: true })) : (React.createElement(CnSafeArea, null)))));
};
SelectDrawer.displayName = 'SelectDrawer';
export default forwardRef(SelectDrawer);
